import * as React from 'react';
import { PermissionProvider, usePermissions, useEntitlement, cn, Badge, Button, Avatar, DataTableColumnHeader, ResourceTable, TableFacets, Input, writeClipboardText, Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, Field, Checkbox, Textarea, DialogFooter, Spinner, EmptyState, RoleEditor as RoleEditor$1, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, ErrorState, Skeleton, AuditTrail, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, ErrorBoundary, notifyWarning, notifySuccess, notifyError, Popover, PopoverTrigger, PopoverContent, useAction as useAction$1, Tabs, TabsList, TabsTrigger, TabsContent, AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from '@authe/ui-core';
export { PermissionMatrixEditor, PermissionScopePicker } from '@authe/ui-core';
import { TeamRoleKey, MemberStatus, MEMBER_STATUS_LABEL, teamKey, TeamResource, memberInitials, isValidEmail, INVITE_STATUS_LABEL, isInviteActionable, grantableTeamScopes, toActivityLogEvents, INVITE_EMAIL_NOT_SENT, sameTeam, resolveRoleKey, InviteStatus } from '@authe/fe-commons/teams';
export { INVITE_EMAIL_NOT_SENT, INVITE_STATUS_LABEL, InviteStatus, MEMBER_STATUS_LABEL, MemberStatus, TeamResource, TeamRoleKey, effectiveMatrix, isInviteActionable, isValidEmail, memberInitials, memberPermissionSet, resolveRoleKey, rolesForMember, sameTeam, teamKey, teamTenantKey, toActivityLogEvents } from '@authe/fe-commons/teams';
import { useAsyncResource } from '@authe/ui-core/api';
export { useAsyncResource } from '@authe/ui-core/api';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { countPermissions, toScopeActions } from '@authe/fe-commons/auth';
export { ACTION_LABEL, DEFAULT_ACTIONS, DEFAULT_RESOURCE_GROUPS, MANAGE_ACTION, countPermissions, enforceFromRolePermissions, grantedGroupCount, groupFullyGranted, hasPermission, mergeMatrices, permissionSetFromRolePermissions, setGroupActions, togglePermission } from '@authe/fe-commons/auth';
import { createLogger } from '@authe/fe-commons/logging';
import { ErrorCode, toErrorResponse, errorCodeOf, remedyForReader, isServerError } from '@authe/fe-commons/errors';
import { bundledPlans, hasEntitlement, ENTITLEMENT } from '@authe/fe-commons/plans';
import { formatRelativeTime } from '@authe/fe-commons/time';
import { unwrapResponse, unwrapData } from '@authe/fe-commons/api';
import { asString } from '@authe/fe-commons/display';

// src/TeamManagementPanel.tsx
var TeamDataContext = React.createContext(null);
var patchMember = (members, id, patch) => (members ?? []).map((member) => member.id === id ? { ...member, ...patch } : member);
function toAuditPage(data) {
  if (!data) return { entries: [] };
  return Array.isArray(data) ? { entries: data } : data;
}
function countMembers(members, roleId) {
  return (members ?? []).reduce((n, member) => member.roleIds.includes(roleId) ? n + 1 : n, 0);
}
function TeamDataProvider({
  api,
  team,
  auditQuery,
  refreshToken,
  children
}) {
  const key = teamKey(team);
  const identity = React.useMemo(() => ({ api, key }), [api, key]);
  const activeIdentity = React.useRef(identity);
  React.useLayoutEffect(() => {
    activeIdentity.current = identity;
    return () => {
      activeIdentity.current = null;
    };
  }, [identity]);
  const isCurrent = React.useCallback(() => activeIdentity.current === identity, [identity]);
  const membersRes = useAsyncResource(() => api.listMembers(team), [api, key]);
  const rolesRes = useAsyncResource(() => api.listRoles(team), [api, key]);
  const invitesRes = useAsyncResource(() => api.listInvites(team), [api, key]);
  const auditRes = useAsyncResource(
    () => api.listAuditEvents(team, auditQuery),
    [api, key, auditQuery?.limit, auditQuery?.cursor]
  );
  const setMembers = membersRes.setData;
  const setRoles = rolesRes.setData;
  const setInvites = invitesRes.setData;
  const refreshAudit = auditRes.refetch;
  const firstPage = React.useMemo(() => toAuditPage(auditRes.data), [auditRes.data]);
  const [older, setOlder] = React.useState({ owner: auditRes.data, entries: [], exhausted: false });
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [loadMoreError, setLoadMoreError] = React.useState(null);
  const paging = React.useMemo(
    () => ({ identity, firstPage: auditRes.data, limit: auditQuery?.limit, cursor: auditQuery?.cursor }),
    [identity, auditRes.data, auditQuery?.limit, auditQuery?.cursor]
  );
  const activePaging = React.useRef(paging);
  const pendingPages = React.useRef(/* @__PURE__ */ new Set());
  React.useLayoutEffect(() => {
    activePaging.current = paging;
  }, [paging]);
  if (older.owner !== auditRes.data) {
    setOlder({ owner: auditRes.data, entries: [], exhausted: false });
    setLoadMoreError(null);
    setIsLoadingMore(false);
  }
  const auditCursor = older.exhausted ? void 0 : older.cursor ?? firstPage.nextCursor;
  const auditEntries = React.useMemo(
    () => [...new Map([...firstPage.entries, ...older.entries].map((entry) => [entry.id, entry])).values()],
    [firstPage.entries, older.entries]
  );
  const loadMoreAudit = React.useCallback(async () => {
    if (!auditCursor || pendingPages.current.has(paging)) return;
    pendingPages.current.add(paging);
    setIsLoadingMore(true);
    setLoadMoreError(null);
    try {
      const page = toAuditPage(
        await api.listAuditEvents(team, { ...auditQuery, cursor: auditCursor })
      );
      if (!isCurrent() || activePaging.current !== paging) return;
      setOlder((prev) => ({
        ...prev,
        entries: [...prev.entries, ...page.entries],
        cursor: page.nextCursor,
        // A page with no cursor is the end. A page that hands back the SAME
        // cursor would otherwise loop forever on the same rows.
        exhausted: !page.nextCursor || page.nextCursor === auditCursor
      }));
    } catch (error) {
      if (!isCurrent() || activePaging.current !== paging) return;
      setLoadMoreError(error ?? new Error("Could not load older activity"));
    } finally {
      pendingPages.current.delete(paging);
      if (isCurrent() && activePaging.current === paging) setIsLoadingMore(false);
    }
  }, [api, team, auditQuery, auditCursor, paging, isCurrent]);
  const actedOn = React.useRef(refreshToken);
  const refetchMembers = membersRes.refetch;
  const refetchRoles = rolesRes.refetch;
  const refetchInvites = invitesRes.refetch;
  React.useEffect(() => {
    if (actedOn.current === refreshToken) return;
    actedOn.current = refreshToken;
    refetchMembers();
    refetchRoles();
    refetchInvites();
    refreshAudit();
  }, [refreshToken, refetchMembers, refetchRoles, refetchInvites, refreshAudit]);
  React.useEffect(() => {
    let lastRefresh = 0;
    const refresh = () => {
      if (document.visibilityState === "hidden" || Date.now() - lastRefresh < 1e3) return;
      lastRefresh = Date.now();
      refetchMembers();
      refetchRoles();
      refetchInvites();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const timer = window.setInterval(refresh, 6e4);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(timer);
    };
  }, [refetchMembers, refetchRoles, refetchInvites]);
  const updateMemberRoles = React.useCallback(
    async (memberId, roleIds) => {
      const updated = await api.updateMemberRoles(team, memberId, roleIds);
      if (!isCurrent()) return;
      setMembers((prev) => patchMember(prev, memberId, { roleIds: updated.roleIds }));
      refreshAudit();
    },
    [api, team, setMembers, refreshAudit, isCurrent]
  );
  const setMemberStatus = React.useCallback(
    async (memberId, status) => {
      const updated = await api.setMemberStatus(team, memberId, status);
      if (!isCurrent()) return;
      setMembers((prev) => patchMember(prev, memberId, { status: updated.status }));
      refreshAudit();
    },
    [api, team, setMembers, refreshAudit, isCurrent]
  );
  const removeMember = React.useCallback(
    async (memberId) => {
      await api.removeMember(team, memberId);
      if (!isCurrent()) return;
      setMembers((prev) => (prev ?? []).filter((member) => member.id !== memberId));
      refreshAudit();
    },
    [api, team, setMembers, refreshAudit, isCurrent]
  );
  const createRole = React.useCallback(
    async (input) => {
      const role = await api.createRole(team, input);
      if (!isCurrent()) return role;
      setRoles((prev) => prev?.some((existing) => existing.id === role.id) ? prev : [...prev ?? [], role]);
      refreshAudit();
      return role;
    },
    [api, team, setRoles, refreshAudit, isCurrent]
  );
  const updateRole = React.useCallback(
    async (roleId, patch) => {
      const role = await api.updateRole(team, roleId, patch);
      if (!isCurrent()) return role;
      setRoles((prev) => (prev ?? []).map((existing) => existing.id === roleId ? role : existing));
      refreshAudit();
      return role;
    },
    [api, team, setRoles, refreshAudit, isCurrent]
  );
  const removeRole = React.useCallback(
    async (roleId) => {
      await api.deleteRole(team, roleId);
      if (!isCurrent()) return;
      setRoles((prev) => (prev ?? []).filter((role) => role.id !== roleId));
      setMembers(
        (prev) => (prev ?? []).map(
          (member) => member.roleIds.includes(roleId) ? { ...member, roleIds: member.roleIds.filter((id) => id !== roleId) } : member
        )
      );
      refreshAudit();
    },
    [api, team, setRoles, setMembers, refreshAudit, isCurrent]
  );
  const canInvite = typeof api.inviteMember === "function";
  const canResend = typeof api.resendInvite === "function";
  const invite = React.useCallback(
    async (input) => {
      if (!api.inviteMember) {
        throw new Error("This backend does not issue invitations.");
      }
      const created = await api.inviteMember(team, input);
      if (!isCurrent()) return created;
      const { acceptPath: _secret, ...listed } = created;
      setInvites((prev) => prev?.some((existing) => existing.id === listed.id) ? prev : [listed, ...prev ?? []]);
      refreshAudit();
      return created;
    },
    [api, team, setInvites, refreshAudit, isCurrent]
  );
  const revokeInvite = React.useCallback(
    async (inviteId) => {
      await api.revokeInvite(team, inviteId);
      if (!isCurrent()) return;
      setInvites(
        (prev) => (prev ?? []).map(
          (existing) => existing.id === inviteId ? { ...existing, status: "revoked" } : existing
        )
      );
      refreshAudit();
    },
    [api, team, setInvites, refreshAudit, isCurrent]
  );
  const resendInvite = React.useCallback(
    async (inviteId) => {
      if (!api.resendInvite) {
        throw new Error("This backend does not re-issue invitations.");
      }
      const updated = await api.resendInvite(team, inviteId);
      if (!isCurrent()) return updated;
      const { acceptPath: _secret, ...listed } = updated;
      setInvites((prev) => (prev ?? []).map((existing) => existing.id === inviteId ? listed : existing));
      refreshAudit();
      return updated;
    },
    [api, team, setInvites, refreshAudit, isCurrent]
  );
  const rolesData = rolesRes.data;
  const membersData = membersRes.data;
  const rolesWithCounts = React.useMemo(() => {
    if (!rolesData) return rolesData;
    if (!membersData) return rolesData;
    return rolesData.map((role) => ({ ...role, memberCount: countMembers(membersData, role.id) }));
  }, [rolesData, membersData]);
  const value = React.useMemo(
    () => ({
      members: {
        members: membersRes.data ?? [],
        // Background reads keep rows and their open controls mounted.
        isLoading: membersRes.isLoading && membersRes.data === void 0,
        error: membersRes.error,
        refetch: membersRes.refetch,
        updateRoles: updateMemberRoles,
        setStatus: setMemberStatus,
        remove: removeMember
      },
      roles: {
        roles: rolesWithCounts ?? [],
        isLoading: rolesRes.isLoading && rolesRes.data === void 0,
        error: rolesRes.error,
        refetch: rolesRes.refetch,
        create: createRole,
        update: updateRole,
        remove: removeRole
      },
      invites: {
        invites: invitesRes.data ?? [],
        isLoading: invitesRes.isLoading && invitesRes.data === void 0,
        error: invitesRes.error,
        refetch: invitesRes.refetch,
        invite,
        revoke: revokeInvite,
        resend: resendInvite,
        canInvite,
        canResend
      },
      audit: {
        entries: auditEntries,
        isLoading: auditRes.isLoading,
        error: auditRes.error,
        refetch: auditRes.refetch,
        hasMore: Boolean(auditCursor),
        isLoadingMore,
        loadMore: loadMoreAudit,
        loadMoreError
      }
    }),
    [
      membersRes.data,
      membersRes.isLoading,
      membersRes.error,
      membersRes.refetch,
      rolesWithCounts,
      rolesRes.isLoading,
      rolesRes.error,
      rolesRes.refetch,
      invitesRes.data,
      invitesRes.isLoading,
      invitesRes.error,
      invitesRes.refetch,
      auditEntries,
      auditRes.isLoading,
      auditRes.error,
      auditRes.refetch,
      auditCursor,
      isLoadingMore,
      loadMoreAudit,
      loadMoreError,
      updateMemberRoles,
      setMemberStatus,
      removeMember,
      createRole,
      updateRole,
      removeRole,
      invite,
      revokeInvite,
      resendInvite,
      canInvite,
      canResend
    ]
  );
  return /* @__PURE__ */ jsx(TeamDataContext.Provider, { value, children });
}
function useTeamData() {
  const value = React.useContext(TeamDataContext);
  if (!value) throw new Error("useTeamData must be used within a <TeamsProvider>");
  return value;
}
var TeamsContext = React.createContext(null);
function TeamsProvider({
  api,
  team,
  currentUserId,
  can,
  permissions,
  rolePermissions,
  ungated = false,
  auditQuery,
  refreshToken,
  children
}) {
  const value = React.useMemo(
    () => ({ api, team, currentUserId }),
    [api, team, currentUserId]
  );
  const gated = Boolean(can || permissions || rolePermissions);
  React.useEffect(() => {
    if (gated || ungated) return;
    warnUngated();
  }, [gated, ungated]);
  const resolvedCan = can ?? (gated ? void 0 : ungated ? allowAll : denyAll);
  return /* @__PURE__ */ jsx(PermissionProvider, { can: resolvedCan, permissions, rolePermissions, children: /* @__PURE__ */ jsx(TeamsContext.Provider, { value, children: /* @__PURE__ */ jsx(
    TeamDataProvider,
    {
      api,
      team,
      auditQuery,
      refreshToken,
      children
    },
    JSON.stringify([teamKey(team), currentUserId ?? null])
  ) }) });
}
var allowAll = () => true;
var denyAll = () => false;
var warnedUngated = false;
function warnUngated() {
  if (warnedUngated) return;
  warnedUngated = true;
  console.warn(
    "[@authe/teams] <TeamsProvider> has no `can`, `permissions` or `rolePermissions`, so every team control is hidden. Pass the signed-in user's permissions, or `ungated` if this surface is deliberately open."
  );
}
function useTeams() {
  const value = React.useContext(TeamsContext);
  if (!value) throw new Error("useTeams must be used within a <TeamsProvider>");
  return value;
}
function useOptionalTeams() {
  return React.useContext(TeamsContext);
}
function useTeamCan() {
  return usePermissions().can;
}

// src/data/useMembers.ts
function useMembers() {
  return useTeamData().members;
}

// src/data/useRoles.ts
function useRoles() {
  return useTeamData().roles;
}

// src/data/useInvites.ts
function useInvites() {
  return useTeamData().invites;
}

// src/data/useTeamAudit.ts
function useTeamAudit() {
  return useTeamData().audit;
}
function mergeDefaultRoles(custom, defaults = []) {
  if (defaults.length === 0) return custom;
  const have = new Set(custom.map((role) => role.name.toLowerCase()));
  return [...custom, ...defaults.filter((role) => !have.has(role.name.toLowerCase()))];
}
var PRIVILEGE_ORDER = [
  TeamRoleKey.OWNER,
  TeamRoleKey.ADMIN,
  TeamRoleKey.MEMBER,
  TeamRoleKey.VIEWER
];
function privilegeRank(role) {
  const key = resolveRoleKey(role.name);
  const index = key ? PRIVILEGE_ORDER.indexOf(key) : -1;
  return index === -1 ? PRIVILEGE_ORDER.length : index;
}
var PLATFORM_ROLE_PREFIX = "default:";
function isPlatformRole(role) {
  return role.id.startsWith(PLATFORM_ROLE_PREFIX);
}
function groupRolesBySource(roles) {
  const platform = roles.filter(isPlatformRole);
  const own = roles.filter((role) => !isPlatformRole(role));
  const systemRoles = own.filter((role) => role.system).sort((a, b) => privilegeRank(a) - privilegeRank(b));
  const customRoles = own.filter((role) => !role.system);
  const groups = [
    { key: "default", label: "Default roles", roles: systemRoles },
    { key: "custom", label: "Custom roles", roles: customRoles },
    {
      key: "platform",
      label: "Platform roles",
      roles: platform,
      note: "Published by this deployment, for reference \u2014 they can\u2019t be given to a team member."
    }
  ];
  return groups.filter((group) => group.roles.length > 0);
}
var TEAMS_PACKAGE = "teams";
var log = createLogger(`fe:${TEAMS_PACKAGE}`);
function customRolesTier(tiers = bundledPlans()) {
  let lastWithheld = -1;
  tiers.forEach((plan, index) => {
    if (!hasEntitlement(plan, ENTITLEMENT.CUSTOM_ROLES)) lastWithheld = index;
  });
  if (lastWithheld < 0) return tiers[tiers.length - 1];
  return tiers.slice(lastWithheld + 1).find((plan) => hasEntitlement(plan, ENTITLEMENT.CUSTOM_ROLES));
}
function customRolesCeiling(tiers = bundledPlans()) {
  const tier = customRolesTier(tiers);
  return tier ? `Custom roles are included in the ${tier.display_name} plan.` : "Custom roles are not included in your plan.";
}
var CUSTOM_ROLES_NOT_INCLUDED = customRolesCeiling();
function useCustomRolesIncluded() {
  return useEntitlement(ENTITLEMENT.CUSTOM_ROLES);
}
function canGrantRole(role, options) {
  if (options.included || role.system) return true;
  return options.heldRoleIds?.includes(role.id) ?? false;
}
var PERM003 = ErrorCode.PERM003.code;
function isCustomRolesRefusal(error) {
  if (error == null) return false;
  const body = toErrorResponse(error).error;
  if (errorCodeOf(body) !== PERM003) return false;
  const missing = body.details?.missing_entitlements;
  return Array.isArray(missing) && missing.includes(ENTITLEMENT.CUSTOM_ROLES);
}
function customRolesRefusalMessage(error) {
  return isCustomRolesRefusal(error) ? CUSTOM_ROLES_NOT_INCLUDED : void 0;
}

// src/data/refusals.ts
var RULE_STATUSES = /* @__PURE__ */ new Set([400, 409, 422]);
function teamRefusalMessage(error) {
  const plan = customRolesRefusalMessage(error);
  if (plan) return plan;
  if (error == null) return void 0;
  const response = toErrorResponse(error);
  if (!RULE_STATUSES.has(response.statusCode)) return void 0;
  const message = (response.error.message ?? "").trim();
  if (!message) return void 0;
  const sentence = message.charAt(0).toUpperCase() + message.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}
function ConfirmButton({
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  successMessage,
  errorMessage,
  disabled = false,
  triggerVariant = "ghost",
  triggerClassName,
  size = "sm",
  children
}) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      if (successMessage) notifySuccess(successMessage);
      setOpen(false);
    } catch (error) {
      log.error("teams confirm action failed", { error: String(error) });
      notifyError(error, {
        ...errorMessage ? { title: errorMessage } : {},
        // The API's own reason for a refused rule (e.g. removing the last
        // owner), not the catalogue's copy for its status code.
        description: teamRefusalMessage(error)
      });
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs(AlertDialog, { open, onOpenChange: (next) => !busy && setOpen(next), children: [
    /* @__PURE__ */ jsx(AlertDialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { type: "button", variant: triggerVariant, size, className: triggerClassName, disabled, children }) }),
    /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
      /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
        /* @__PURE__ */ jsx(AlertDialogTitle, { children: title }),
        description ? /* @__PURE__ */ jsx(AlertDialogDescription, { children: description }) : null
      ] }),
      /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
        /* @__PURE__ */ jsx(AlertDialogCancel, { disabled: busy, children: cancelLabel }),
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            variant: destructive ? "destructive" : "default",
            disabled: busy,
            onClick: () => void confirm(),
            children: busy ? /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2 text-current" }),
              confirmLabel,
              "\u2026"
            ] }) : confirmLabel
          }
        )
      ] })
    ] })
  ] });
}
var UNNAMED = "Unknown role";
function RoleChips({
  roleIds,
  roles,
  emptyLabel = "No roles",
  className
}) {
  const byId = React.useMemo(() => new Map(roles.map((role) => [role.id, role])), [roles]);
  if (roleIds.length === 0) {
    return /* @__PURE__ */ jsx("span", { className: cn("text-xs text-muted-foreground", className), children: emptyLabel });
  }
  if (roles.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap gap-1", className), title: "Role names aren\u2019t available yet", children: roleIds.map((id) => /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "font-normal text-muted-foreground", children: "\u2026" }, id)) });
  }
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap gap-1", className), children: roleIds.map((id) => {
    const role = byId.get(id);
    return /* @__PURE__ */ jsx(
      Badge,
      {
        variant: role?.system ? "secondary" : "outline",
        className: cn("font-normal", role ? null : "text-muted-foreground"),
        title: role ? void 0 : id,
        children: role?.name ?? UNNAMED
      },
      id
    );
  }) });
}
function RolesUnavailableNotice({
  error,
  onRetry,
  className
}) {
  if (error == null) return null;
  const { retryable, action } = remedyForReader(error);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "alert",
      className: cn(
        "mb-2 flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("span", { children: [
          /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: retryable ? "Roles couldn\u2019t be loaded." : "You can\u2019t see this team\u2019s roles." }),
          " ",
          "Role names below may show as ids, and role changes are unavailable. ",
          action
        ] }),
        retryable && onRetry ? /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", size: "sm", className: "h-6 px-2", onClick: onRetry, children: "Retry" }) : null
      ]
    }
  );
}
function relTime(iso) {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  return formatRelativeTime(ms, { unit: "milliseconds" });
}
function useAction(fn, options = {}) {
  return useAction$1(fn, { log, describeError: teamRefusalMessage, ...options });
}
var STATUS_STYLE = {
  active: "",
  invited: "text-muted-foreground",
  suspended: "text-risk-high"
};
var COLUMN_LABELS = {
  member: "Member",
  roles: "Roles",
  status: "Status",
  joined: "Joined"
};
var STATUS_FACET = {
  column: "status",
  title: "Status",
  options: Object.values(MemberStatus).map((status) => ({
    // Filter on the LABEL, matching what the status column accessor returns —
    // otherwise every option selects nothing.
    value: MEMBER_STATUS_LABEL[status],
    label: MEMBER_STATUS_LABEL[status]
  }))
};
function RolePopover({
  member,
  roles,
  onUpdateRoles
}) {
  const [run, busy] = useAction(onUpdateRoles, { error: "Couldn't update roles." });
  const included = useCustomRolesIncluded();
  const grantable = (role) => canGrantRole(role, { included, heldRoleIds: member.roleIds });
  const withheld = roles.some((role) => !grantable(role));
  const toggle = (roleId) => {
    const next = member.roleIds.includes(roleId) ? member.roleIds.filter((id) => id !== roleId) : [...member.roleIds, roleId];
    run(member.id, next);
  };
  return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "rounded-md text-left outline-none ring-offset-background hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring",
        "aria-label": `Edit roles for ${member.name}`,
        children: /* @__PURE__ */ jsx(RoleChips, { roleIds: member.roleIds, roles })
      }
    ) }),
    /* @__PURE__ */ jsxs(PopoverContent, { align: "start", className: "w-64", children: [
      /* @__PURE__ */ jsx("p", { className: "mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground", children: "Roles" }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
        groupRolesBySource(roles).flatMap((group) => group.roles).map((role) => {
          const offered = grantable(role);
          return /* @__PURE__ */ jsxs(
            "label",
            {
              className: cn("flex items-center gap-2 text-sm", offered ? null : "text-muted-foreground"),
              children: [
                /* @__PURE__ */ jsx(
                  Checkbox,
                  {
                    checked: member.roleIds.includes(role.id),
                    disabled: busy || !offered,
                    onCheckedChange: () => toggle(role.id)
                  }
                ),
                /* @__PURE__ */ jsx("span", { className: "flex-1", children: role.name }),
                role.system ? /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "system" }) : null
              ]
            },
            role.id
          );
        }),
        roles.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No roles defined yet." }) : null,
        withheld ? /* @__PURE__ */ jsxs("p", { className: "mt-1 text-xs text-muted-foreground", children: [
          CUSTOM_ROLES_NOT_INCLUDED,
          " Custom roles they already hold can be kept or removed."
        ] }) : null
      ] })
    ] })
  ] });
}
function MemberActions({
  member,
  currentUserId,
  canEdit,
  canRemove,
  onSetStatus,
  onRemove
}) {
  const [runStatus, statusBusy] = useAction(onSetStatus, {
    success: "Member status updated",
    error: "Couldn't update the member."
  });
  const isSelf = member.id === currentUserId;
  const suspended = member.status === MemberStatus.SUSPENDED;
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-end gap-1", children: [
    canEdit && onSetStatus && !isSelf && member.status !== MemberStatus.INVITED ? suspended ? /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "ghost",
        size: "sm",
        disabled: statusBusy,
        onClick: () => runStatus(member.id, MemberStatus.ACTIVE),
        children: "Reactivate"
      }
    ) : /* @__PURE__ */ jsx(
      ConfirmButton,
      {
        title: `Suspend ${member.name}?`,
        description: "They keep their membership but lose access until reactivated.",
        confirmLabel: "Suspend",
        successMessage: "Member suspended",
        errorMessage: "Couldn't suspend the member.",
        onConfirm: () => onSetStatus(member.id, MemberStatus.SUSPENDED),
        children: "Suspend"
      }
    ) : null,
    canRemove && onRemove && !isSelf ? /* @__PURE__ */ jsx(
      ConfirmButton,
      {
        destructive: true,
        triggerClassName: "text-risk-high",
        title: `Remove ${member.name}?`,
        description: "They'll lose access to this team immediately. You can invite them again later.",
        confirmLabel: "Remove",
        successMessage: "Member removed",
        errorMessage: "Couldn't remove the member.",
        onConfirm: () => onRemove(member.id),
        children: "Remove"
      }
    ) : null
  ] });
}
function MembersTable({
  members,
  roles,
  rolesError,
  onRetryRoles,
  currentUserId,
  onUpdateRoles,
  onSetStatus,
  onRemove,
  isLoading,
  error,
  onRetry,
  emptyAction,
  height = "max-h-[520px]",
  className
}) {
  const can = useTeamCan();
  const canEdit = can(TeamResource.MEMBERS, "edit");
  const canRemove = can(TeamResource.MEMBERS, "delete");
  const columns = React.useMemo(
    () => [
      // Widths go on the fixed-content columns only; the member column takes
      // what is left. With none declared, `table-layout: fixed` split the table
      // into five equal columns — names truncated to "Roel d…" while the two
      // row actions overflowed theirs, pushing "Remove" out of sight behind a
      // horizontal scroll at a 1024px window.
      {
        id: "member",
        // An avatar, a name and "(you)" need room: below this the table
        // scrolls sideways rather than crushing the name to "R..". 180 with the
        // fixed columns (500) still fits the 684px card at a 1024px window.
        minSize: 180,
        // Name AND email, so one search box finds a member by either.
        accessorFn: (member) => `${member.name} ${member.email ?? ""}`,
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Member" }),
        cell: ({ row }) => {
          const member = row.original;
          const isSelf = member.id === currentUserId;
          return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(
              Avatar,
              {
                src: member.avatarUrl,
                alt: member.name,
                size: "sm",
                fallback: memberInitials(member)
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
                /* @__PURE__ */ jsx("span", { className: "truncate text-sm font-medium", title: member.name, children: member.name }),
                isSelf ? /* @__PURE__ */ jsx("span", { className: "shrink-0 text-xs text-muted-foreground", children: "(you)" }) : null
              ] }),
              member.email ? /* @__PURE__ */ jsx("span", { className: "block truncate text-xs text-muted-foreground", title: member.email, children: member.email }) : null
            ] })
          ] });
        }
      },
      {
        id: "roles",
        size: 120,
        // Role NAMES, not ids, so the search box matches what the chips show.
        accessorFn: (member) => member.roleIds.map((id) => roles.find((role) => role.id === id)?.name ?? id).join(" "),
        header: "Roles",
        enableSorting: false,
        cell: ({ row }) => canEdit && onUpdateRoles ? /* @__PURE__ */ jsx(RolePopover, { member: row.original, roles, onUpdateRoles }) : /* @__PURE__ */ jsx(RoleChips, { roleIds: row.original.roleIds, roles })
      },
      {
        id: "status",
        size: 100,
        accessorFn: (member) => MEMBER_STATUS_LABEL[member.status],
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Status" }),
        cell: ({ row }) => /* @__PURE__ */ jsx(
          Badge,
          {
            variant: "outline",
            className: cn("font-normal", STATUS_STYLE[row.original.status]),
            children: MEMBER_STATUS_LABEL[row.original.status]
          }
        )
      },
      {
        // "Joined", not "Last active". `TeamMember.last_active_at` exists on the
        // model but nothing on the platform ever writes it, so the column it
        // used to back was blank for every member of every team, forever — a
        // header promising a fact the product does not collect. `joined_at` is
        // stamped when the membership is created (team seed / invite accept),
        // so this one always has an answer.
        id: "joined",
        size: 110,
        accessorFn: (member) => member.joinedAt ?? "",
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Joined" }),
        cell: ({ row }) => /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: relTime(row.original.joinedAt) })
      },
      {
        id: "actions",
        size: 170,
        header: "",
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => /* @__PURE__ */ jsx(
          MemberActions,
          {
            member: row.original,
            currentUserId,
            canEdit,
            canRemove,
            onSetStatus,
            onRemove
          }
        )
      }
    ],
    [roles, currentUserId, canEdit, canRemove, onUpdateRoles, onSetStatus, onRemove]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(RolesUnavailableNotice, { error: rolesError, onRetry: onRetryRoles }),
    /* @__PURE__ */ jsx(
      ResourceTable,
      {
        className,
        rows: members,
        columns,
        getRowId: (member) => member.id,
        isLoading,
        error,
        onRetry,
        toolbar: {
          searchColumns: ["member", "roles"],
          searchPlaceholder: "Search members\u2026",
          columnLabels: COLUMN_LABELS
        },
        facets: (table) => /* @__PURE__ */ jsx(TableFacets, { table, configs: [STATUS_FACET] }),
        height,
        emptyTitle: "No members yet",
        emptyDescription: "Invite teammates to collaborate on this team.",
        emptyAction
      }
    )
  ] });
}
function InviteAcceptanceLink({ path }) {
  const [copied, setCopied] = React.useState(false);
  const [error, setError] = React.useState(false);
  if (!path.startsWith("/teams/accept-invite?token=")) return null;
  const url = new URL(path, window.location.origin).href;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2 rounded border p-3 text-sm", children: [
    /* @__PURE__ */ jsx("p", { children: "Share this link privately with the invited person. They must sign in with the invited email to accept." }),
    /* @__PURE__ */ jsx(Input, { "aria-label": "Invitation acceptance link", readOnly: true, value: url, onFocus: (event) => event.target.select() }),
    /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", onClick: async () => {
      const ok = await writeClipboardText(url);
      setCopied(ok);
      setError(!ok);
    }, children: copied ? "Copied" : "Copy acceptance link" }),
    error ? /* @__PURE__ */ jsx("p", { role: "alert", children: "Copy unavailable. Select and copy the link above." }) : null
  ] });
}
function optionLabel(person) {
  const name = person.name && person.name !== person.email ? person.name : void 0;
  if (!person.outsideTenant) return name;
  return name ? `${name} \u2014 no roles in this tenant` : "No roles in this tenant";
}
function findSuggestion(offered, email) {
  const wanted = email.trim().toLowerCase();
  if (!wanted) return void 0;
  return offered.find((person) => person.email.trim().toLowerCase() === wanted);
}
function InviteMemberDialog({
  roles,
  suggestions,
  onInvite,
  trigger,
  open,
  onOpenChange,
  defaultRoleIds = []
}) {
  const can = useTeamCan();
  const customRolesIncluded = useCustomRolesIncluded();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = open !== void 0;
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = React.useCallback(
    (next) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );
  const [email, setEmail] = React.useState("");
  const [roleIds, setRoleIds] = React.useState(defaultRoleIds);
  const [message, setMessage] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [acceptPath, setAcceptPath] = React.useState(null);
  React.useEffect(() => {
    if (isOpen) {
      setEmail("");
      setRoleIds(
        defaultRoleIds.filter((id) => {
          const role = roles.find((candidate) => candidate.id === id);
          return !role || canGrantRole(role, { included: customRolesIncluded });
        })
      );
      setMessage("");
      setTouched(false);
      setAttempted(false);
      setSubmitError(null);
      setAcceptPath(null);
    }
  }, [isOpen]);
  const listId = React.useId();
  if (!can(TeamResource.MEMBERS, "create")) return null;
  const offered = suggestions ?? [];
  const chosen = findSuggestion(offered, email);
  const emailValid = isValidEmail(email);
  const rolesValid = roleIds.length > 0;
  const toggleRole = (roleId) => setRoleIds((prev) => prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]);
  const submit = async (event) => {
    event.preventDefault();
    setTouched(true);
    setAttempted(true);
    if (!emailValid || !rolesValid) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const created = await onInvite({ email: email.trim(), roleIds, message: message.trim() || void 0 });
      if (created?.emailSent === false) {
        notifyWarning("Invitation created \u2014 email not sent", INVITE_EMAIL_NOT_SENT);
      } else {
        notifySuccess("Invitation sent");
      }
      const path = created?.acceptPath;
      if (path) setAcceptPath(path);
      else setOpen(false);
    } catch (error) {
      log.error("teams invite failed", { error: String(error) });
      const planMessage = customRolesRefusalMessage(error);
      const fromServer = isServerError(error) ? toErrorResponse(error).error.message?.trim() || null : null;
      const explained = planMessage ?? fromServer;
      notifyError(error, {
        title: "Couldn't send the invitation.",
        description: explained ?? void 0
      });
      setSubmitError(explained ?? "Couldn't send the invitation.");
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs(Dialog, { open: isOpen, onOpenChange: (next) => !busy && setOpen(next), children: [
    /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: trigger ?? /* @__PURE__ */ jsx(Button, { type: "button", children: "Invite member" }) }),
    /* @__PURE__ */ jsx(DialogContent, { children: /* @__PURE__ */ jsxs("form", { onSubmit: (event) => void submit(event), children: [
      /* @__PURE__ */ jsxs(DialogHeader, { children: [
        /* @__PURE__ */ jsx(DialogTitle, { children: "Invite member" }),
        /* @__PURE__ */ jsx(DialogDescription, { children: "Invite someone from your organization. They get an email and join once they accept." })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "my-4 flex flex-col gap-4", children: [
        /* @__PURE__ */ jsx(
          Field,
          {
            label: "Email",
            error: touched && !emailValid ? "Enter a valid email address." : void 0,
            hint: suggestions ? offered.length > 0 ? "Start typing to pick someone from your organization." : "Everyone in your organization is already on this team or invited." : void 0,
            children: /* @__PURE__ */ jsx(
              Input,
              {
                type: "email",
                placeholder: "teammate@company.com",
                value: email,
                autoFocus: true,
                list: offered.length > 0 ? listId : void 0,
                onChange: (event) => setEmail(event.target.value),
                onBlur: () => setTouched(true)
              }
            )
          }
        ),
        offered.length > 0 ? /* @__PURE__ */ jsx("datalist", { id: listId, children: offered.map((person) => /* @__PURE__ */ jsx("option", { value: person.email, children: optionLabel(person) }, person.email)) }) : null,
        chosen?.outsideTenant ? (
          // Said before Send, not discovered afterwards on a roster that
          // looks perfectly normal. See `InviteSuggestion.outsideTenant`.
          /* @__PURE__ */ jsxs("p", { className: "text-xs text-amber-700 dark:text-amber-400", role: "status", children: [
            chosen.name ? `${chosen.name} holds` : "This person holds",
            " no roles in the tenant this team belongs to, so a seat here would give them nothing \u2014 they could not open the team or see anything shared with it. Grant them a role in this tenant first."
          ] })
        ) : null,
        /* @__PURE__ */ jsx(
          Field,
          {
            label: "Roles",
            error: attempted && !rolesValid ? "Pick at least one role \u2014 a member with none can\u2019t open this team or work on anything in it." : void 0,
            children: /* @__PURE__ */ jsx("div", { className: "flex max-h-56 flex-col gap-3 overflow-y-auto rounded-md border p-2", children: roles.length === 0 ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "No roles defined yet." }) : groupRolesBySource(roles).map((group) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-medium uppercase tracking-wide text-muted-foreground", children: group.label }),
              group.roles.map((role) => {
                const offered2 = canGrantRole(role, { included: customRolesIncluded });
                const checked = roleIds.includes(role.id);
                return /* @__PURE__ */ jsxs(
                  "label",
                  {
                    className: cn("flex items-start gap-2 text-sm", offered2 ? null : "text-muted-foreground"),
                    children: [
                      /* @__PURE__ */ jsx(
                        Checkbox,
                        {
                          className: "mt-0.5 shrink-0",
                          checked,
                          disabled: !offered2 && !checked,
                          onCheckedChange: () => toggleRole(role.id)
                        }
                      ),
                      /* @__PURE__ */ jsxs("span", { className: "min-w-0", children: [
                        /* @__PURE__ */ jsx("span", { className: "block font-medium", children: role.name }),
                        role.description ? /* @__PURE__ */ jsx("span", { className: "block text-xs text-muted-foreground", children: role.description }) : null
                      ] })
                    ]
                  },
                  role.id
                );
              }),
              group.key === "custom" && !customRolesIncluded ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: CUSTOM_ROLES_NOT_INCLUDED }) : null
            ] }, group.key)) })
          }
        ),
        /* @__PURE__ */ jsx(Field, { label: "Message (optional)", children: /* @__PURE__ */ jsx(
          Textarea,
          {
            rows: 2,
            placeholder: "Add a note to the invitation email\u2026",
            value: message,
            onChange: (event) => setMessage(event.target.value)
          }
        ) })
      ] }),
      submitError ? /* @__PURE__ */ jsx("p", { role: "alert", className: "mb-2 text-sm text-destructive", children: submitError }) : null,
      acceptPath ? /* @__PURE__ */ jsx(InviteAcceptanceLink, { path: acceptPath }) : null,
      /* @__PURE__ */ jsxs(DialogFooter, { children: [
        /* @__PURE__ */ jsx(Button, { type: "button", variant: "ghost", disabled: busy, onClick: () => setOpen(false), children: acceptPath ? "Done" : "Cancel" }),
        /* @__PURE__ */ jsx(Button, { type: "submit", disabled: busy || Boolean(acceptPath) || touched && !emailValid, children: busy ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2 text-current" }),
          "Sending\u2026"
        ] }) : "Send invite" })
      ] })
    ] }) })
  ] });
}
var STATUS_VARIANT = {
  pending: "secondary",
  accepted: "outline",
  revoked: "destructive",
  expired: "outline"
};
var COLUMN_LABELS2 = {
  email: "Email",
  roles: "Roles",
  status: "Status"
};
function inviteTimeline(invite) {
  const sent = invite.invitedAt ? `Invited ${relTime(invite.invitedAt)}` : "";
  const expiry = invite.expiresAt && (invite.status === InviteStatus.PENDING || invite.status === InviteStatus.EXPIRED) ? `${Date.parse(invite.expiresAt) > Date.now() ? "expires" : "expired"} ${relTime(invite.expiresAt)}` : "";
  return [sent, expiry].filter(Boolean).join(" \xB7 ");
}
function byNewest(a, b) {
  return (Date.parse(b.invitedAt ?? "") || 0) - (Date.parse(a.invitedAt ?? "") || 0);
}
function resendGrantsWithheldRole(invite, roles, included) {
  if (included) return false;
  return invite.roleIds.some((id) => {
    const role = roles.find((candidate) => candidate.id === id);
    return role !== void 0 && !canGrantRole(role, { included });
  });
}
function InviteActions({
  invite,
  canRevoke,
  canResend,
  onRevoke,
  onResend
}) {
  const [acceptPath, setAcceptPath] = React.useState(null);
  const resendAndReport = React.useCallback(
    async (inviteId) => {
      const updated = await onResend?.(inviteId);
      setAcceptPath(updated?.acceptPath ?? null);
      if (updated?.emailSent === false) {
        notifyWarning("Invitation renewed \u2014 email not sent", INVITE_EMAIL_NOT_SENT);
      } else {
        notifySuccess("Invitation resent");
      }
    },
    [onResend]
  );
  const [runResend, resendBusy] = useAction(onResend ? resendAndReport : void 0, {
    error: "Couldn't resend the invitation."
  });
  if (!isInviteActionable(invite) || !canResend && !canRevoke) return null;
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-end gap-1", children: [
    acceptPath ? /* @__PURE__ */ jsx(InviteAcceptanceLink, { path: acceptPath }) : null,
    canResend && onResend ? /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "ghost",
        size: "sm",
        disabled: resendBusy,
        onClick: () => runResend(invite.id),
        children: "Resend"
      }
    ) : null,
    canRevoke && onRevoke ? /* @__PURE__ */ jsx(
      ConfirmButton,
      {
        destructive: true,
        triggerClassName: "text-risk-high",
        title: `Revoke invite to ${invite.email}?`,
        description: "The invitation link stops working immediately. You can always send a new invite later.",
        confirmLabel: "Revoke invite",
        successMessage: "Invitation revoked",
        errorMessage: "Couldn't revoke the invitation.",
        onConfirm: () => onRevoke(invite.id),
        children: "Revoke"
      }
    ) : null
  ] });
}
function InvitesTable({
  invites,
  roles,
  rolesError,
  onRetryRoles,
  onRevoke,
  onResend,
  isLoading,
  error,
  onRetry,
  emptyAction,
  height = "max-h-[420px]",
  className
}) {
  const can = useTeamCan();
  const canRevoke = can(TeamResource.INVITES, "delete");
  const canResend = can(TeamResource.INVITES, "create");
  const customRolesIncluded = useCustomRolesIncluded();
  const columns = React.useMemo(
    () => [
      // Same rule as the members table: fixed widths on the fixed-content
      // columns, the email takes the rest (equal columns clipped the actions).
      {
        id: "email",
        // Room for an address and its timeline before the table scrolls sideways.
        minSize: 200,
        accessorFn: (invite) => invite.email,
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Email" }),
        cell: ({ row }) => /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx("div", { className: "truncate text-sm font-medium", title: row.original.email, children: row.original.email }),
          /* @__PURE__ */ jsx("div", { className: "text-xs text-muted-foreground", children: inviteTimeline(row.original) })
        ] })
      },
      {
        id: "roles",
        size: 120,
        // Role NAMES, not ids, so the search box matches what the chips show.
        accessorFn: (invite) => invite.roleIds.map((id) => roles.find((role) => role.id === id)?.name ?? id).join(" "),
        header: "Roles",
        enableSorting: false,
        cell: ({ row }) => /* @__PURE__ */ jsx(RoleChips, { roleIds: row.original.roleIds, roles })
      },
      {
        id: "status",
        size: 100,
        accessorFn: (invite) => INVITE_STATUS_LABEL[invite.status],
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Status" }),
        cell: ({ row }) => /* @__PURE__ */ jsx(Badge, { variant: STATUS_VARIANT[row.original.status], className: "font-normal", children: INVITE_STATUS_LABEL[row.original.status] })
      },
      {
        id: "actions",
        size: 170,
        header: "",
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => /* @__PURE__ */ jsx(
          InviteActions,
          {
            invite: row.original,
            canRevoke,
            canResend: canResend && !resendGrantsWithheldRole(row.original, roles, customRolesIncluded),
            onRevoke,
            onResend
          }
        )
      }
    ],
    [roles, canRevoke, canResend, customRolesIncluded, onRevoke, onResend]
  );
  const sortedInvites = React.useMemo(() => [...invites].sort(byNewest), [invites]);
  const resendWithheld = canResend && Boolean(onResend) && invites.some(
    (invite) => isInviteActionable(invite) && resendGrantsWithheldRole(invite, roles, customRolesIncluded)
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(RolesUnavailableNotice, { error: rolesError, onRetry: onRetryRoles }),
    resendWithheld ? /* @__PURE__ */ jsxs("p", { className: "mb-2 rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground", children: [
      CUSTOM_ROLES_NOT_INCLUDED,
      " Invitations that carry a custom role can\u2019t be resent, but they can still be revoked."
    ] }) : null,
    /* @__PURE__ */ jsx(
      ResourceTable,
      {
        className,
        rows: sortedInvites,
        columns,
        getRowId: (invite) => invite.id,
        isLoading,
        error,
        onRetry,
        toolbar: {
          searchColumns: ["email", "roles", "status"],
          searchPlaceholder: "Search invites\u2026",
          columnLabels: COLUMN_LABELS2
        },
        height,
        emptyTitle: "No invitations yet",
        emptyDescription: "Invitations you send appear here, with whether each was accepted, revoked or has expired.",
        emptyAction
      }
    )
  ] });
}
function plural(count, noun) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}
function grantCount(role) {
  return countPermissions(toScopeActions(role.permissions));
}
function RolesList({
  roles,
  selectedId,
  onSelect,
  onCreate,
  onDelete,
  className
}) {
  const can = useTeamCan();
  const canCreate = can(TeamResource.ROLES, "create");
  const canDelete = can(TeamResource.ROLES, "delete");
  const customRolesIncluded = useCustomRolesIncluded();
  const sections = groupRolesBySource(roles);
  const renderRole = (role) => {
    const selected = role.id === selectedId;
    return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex items-center gap-2 rounded-md border p-2 transition-colors",
          selected ? "border-brand bg-muted" : "hover:bg-muted/60"
        ),
        children: [
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: () => onSelect?.(role.id),
              className: "min-w-0 flex-1 text-left outline-none",
              children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
                  /* @__PURE__ */ jsx("span", { className: "truncate text-sm font-medium", children: role.name }),
                  role.system ? /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "system" }) : null
                ] }),
                role.description ? /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-muted-foreground", children: role.description }) : null,
                /* @__PURE__ */ jsxs("div", { className: "mt-0.5 flex flex-wrap gap-2 text-xs text-muted-foreground", children: [
                  /* @__PURE__ */ jsx("span", { children: plural(grantCount(role), "grant") }),
                  typeof role.memberCount === "number" ? /* @__PURE__ */ jsxs("span", { children: [
                    "\xB7 ",
                    plural(role.memberCount, "member")
                  ] }) : null
                ] })
              ]
            }
          ),
          canDelete && onDelete && !role.system ? /* @__PURE__ */ jsx(
            ConfirmButton,
            {
              destructive: true,
              triggerClassName: "text-risk-high",
              title: `Delete \u201C${role.name}\u201D?`,
              description: "This role and its permissions will be removed. Members currently holding it lose those grants. This can't be undone.",
              confirmLabel: "Delete role",
              successMessage: "Role deleted",
              errorMessage: "Couldn't delete the role.",
              onConfirm: () => onDelete(role.id),
              children: "Delete"
            }
          ) : null
        ]
      }
    ) }, role.id);
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    canCreate && onCreate ? customRolesIncluded ? /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", size: "sm", className: "self-start", onClick: onCreate, children: "New role" }) : /* @__PURE__ */ jsxs("p", { className: "rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground", children: [
      CUSTOM_ROLES_NOT_INCLUDED,
      " Existing custom roles keep working and can still be deleted."
    ] }) : null,
    sections.length === 0 ? /* @__PURE__ */ jsx(
      EmptyState,
      {
        size: "sm",
        title: "No roles",
        description: (
          // Only advise creating one to a reader who can: the plan and the
          // permission both decide whether "New role" is above this.
          customRolesIncluded && canCreate && onCreate ? "Create a role to start granting permissions." : "This team has no roles yet."
        )
      }
    ) : /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1", children: sections.map((section) => /* @__PURE__ */ jsxs(React.Fragment, { children: [
      /* @__PURE__ */ jsxs("li", { role: "presentation", className: "flex items-center gap-2 px-1 pb-0.5 pt-3 first:pt-0", children: [
        /* @__PURE__ */ jsx("span", { className: "text-[10px] font-medium uppercase tracking-wide text-muted-foreground", children: section.label }),
        /* @__PURE__ */ jsx("span", { className: "h-px flex-1 bg-border" })
      ] }),
      section.note ? /* @__PURE__ */ jsx("li", { role: "presentation", className: "px-1 pb-1 text-[11px] text-muted-foreground", children: section.note }) : null,
      section.roles.map((role) => renderRole(role))
    ] }, section.key)) })
  ] });
}
function RoleEditor({ role, readOnly, ...rest }) {
  const can = useTeamCan();
  const customRolesIncluded = useCustomRolesIncluded();
  const frozenByPlan = !role.system && !customRolesIncluded;
  const locked = Boolean(readOnly) || !can(TeamResource.ROLES, "edit") || frozenByPlan;
  const editor = /* @__PURE__ */ jsx(RoleEditor$1, { role, readOnly: locked, ...rest });
  if (!frozenByPlan) return editor;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
    /* @__PURE__ */ jsxs("p", { className: "rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground", children: [
      CUSTOM_ROLES_NOT_INCLUDED,
      " This role can\u2019t be edited, but it keeps working for the people who hold it and can still be deleted."
    ] }),
    editor
  ] });
}
function RoleMembers({
  roleId,
  roleSystem = false,
  members,
  membersError,
  membersLoading,
  onRetryMembers,
  onUpdateRoles,
  className
}) {
  const can = useTeamCan();
  const canAssign = can(TeamResource.MEMBERS, "edit");
  const customRolesIncluded = useCustomRolesIncluded();
  const canGrant = roleSystem || customRolesIncluded;
  const [runAssign, assignBusy] = useAction(onUpdateRoles, {
    success: "Member assigned to role",
    error: "Couldn't assign the role."
  });
  const [runUnassign, unassignBusy] = useAction(onUpdateRoles, {
    success: "Member removed from role",
    error: "Couldn't update the role."
  });
  const busy = assignBusy || unassignBusy;
  const assigned = members.filter((member) => member.roleIds.includes(roleId));
  const assignable = members.filter((member) => !member.roleIds.includes(roleId));
  const assign = (member) => {
    runAssign(member.id, [...member.roleIds, roleId]);
  };
  const unassign = (member) => {
    runUnassign(member.id, member.roleIds.filter((id) => id !== roleId));
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("span", { className: "text-sm font-medium", children: [
        "Users with this role (",
        assigned.length,
        ")"
      ] }),
      canAssign && onUpdateRoles && !canGrant ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: CUSTOM_ROLES_NOT_INCLUDED }) : null,
      canAssign && onUpdateRoles && canGrant ? /* @__PURE__ */ jsxs(DropdownMenu, { children: [
        /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", size: "sm", disabled: busy || assignable.length === 0, children: "Assign member" }) }),
        /* @__PURE__ */ jsx(DropdownMenuContent, { align: "end", className: "max-h-72 w-56 overflow-y-auto", children: assignable.length === 0 ? /* @__PURE__ */ jsx(DropdownMenuItem, { disabled: true, children: "Everyone already has this role" }) : assignable.map((member) => /* @__PURE__ */ jsxs(DropdownMenuItem, { onSelect: () => assign(member), children: [
          /* @__PURE__ */ jsx(Avatar, { size: "sm", src: member.avatarUrl, alt: member.name, fallback: memberInitials(member) }),
          /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1 truncate", children: member.name || member.email || member.id })
        ] }, member.id)) })
      ] }) : null
    ] }),
    membersError != null ? (
      // Not "No users": who holds this role is unknown, not empty.
      /* @__PURE__ */ jsx(
        ErrorState,
        {
          size: "sm",
          error: membersError,
          onRetry: onRetryMembers,
          title: "Couldn\u2019t load who holds this role \u2014 the team's member list didn't load, so this role's assignments are unknown."
        }
      )
    ) : membersLoading ? /* @__PURE__ */ jsx(Skeleton, { className: "h-16 w-full" }) : assigned.length === 0 ? /* @__PURE__ */ jsx(EmptyState, { size: "sm", title: "No users", description: "Assign this role to members to grant its permissions." }) : /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1", children: assigned.map((member) => /* @__PURE__ */ jsxs(
      "li",
      {
        className: "flex items-center gap-2 rounded-md border p-2",
        children: [
          /* @__PURE__ */ jsx(Avatar, { size: "sm", src: member.avatarUrl, alt: member.name, fallback: memberInitials(member) }),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx("div", { className: "truncate text-sm font-medium", children: member.name || member.email || member.id }),
            member.email ? /* @__PURE__ */ jsx("div", { className: "truncate text-xs text-muted-foreground", children: member.email }) : null
          ] }),
          canAssign && onUpdateRoles ? /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              variant: "ghost",
              size: "sm",
              disabled: busy,
              "aria-label": `Remove role from ${member.name || member.email || member.id}`,
              onClick: () => unassign(member),
              children: "Remove"
            }
          ) : null
        ]
      },
      member.id
    )) })
  ] });
}
var BLANK_ROLE = { id: "__new__", name: "", permissions: {} };
function RolesPanel({
  resourceGroups,
  actions,
  scopes,
  defaultRoles,
  className
}) {
  const { roles, isLoading, error, refetch, create, update, remove } = useRoles();
  const members = useMembers();
  const [selectedId, setSelectedId] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const customRolesIncluded = useCustomRolesIncluded();
  React.useEffect(() => {
    if (!customRolesIncluded) setCreating(false);
  }, [customRolesIncluded]);
  const displayRoles = React.useMemo(() => mergeDefaultRoles(roles, defaultRoles), [roles, defaultRoles]);
  const teamScopes = React.useMemo(
    () => scopes ? grantableTeamScopes(scopes) : void 0,
    [scopes]
  );
  const selectedRole = creating ? BLANK_ROLE : displayRoles.find((role) => role.id === selectedId) ?? null;
  const selectedIsRealRole = Boolean(
    selectedRole && !creating && roles.some((role) => role.id === selectedRole.id)
  );
  const handleSave = async (draft) => {
    try {
      if (creating) {
        const created = await create(draft);
        setCreating(false);
        setSelectedId(created.id);
        notifySuccess("Role created", { description: `\u201C${created.name}\u201D is ready to assign.` });
      } else if (selectedId) {
        const updated = await update(selectedId, draft);
        notifySuccess("Role updated", { description: `Saved changes to \u201C${updated.name}\u201D.` });
      }
    } catch (err) {
      notifyError(err, { title: "Couldn't save the role.", description: teamRefusalMessage(err) });
    }
  };
  if (error && !roles.length && !creating) return /* @__PURE__ */ jsx(ErrorState, { error, onRetry: refetch });
  if (isLoading && !roles.length && !creating) {
    return /* @__PURE__ */ jsxs("div", { className: "grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]", "aria-busy": true, children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-48 w-full" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-48 w-full" })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]", className), children: [
    error ? /* @__PURE__ */ jsx("div", { className: "sm:col-span-2", children: /* @__PURE__ */ jsx(ErrorState, { error, onRetry: refetch }) }) : null,
    /* @__PURE__ */ jsx(
      RolesList,
      {
        roles: displayRoles,
        selectedId: creating ? null : selectedId,
        onSelect: (id) => {
          setCreating(false);
          setSelectedId(id);
        },
        onCreate: () => {
          setCreating(true);
          setSelectedId(null);
        },
        onDelete: async (id) => {
          await remove(id);
          if (id === selectedId) setSelectedId(null);
        }
      }
    ),
    selectedRole ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-6", children: [
      /* @__PURE__ */ jsx(
        RoleEditor,
        {
          role: selectedRole,
          resourceGroups,
          actions,
          scopes: teamScopes,
          readOnly: selectedRole.system || Boolean(error),
          onSave: handleSave,
          onCancel: creating ? () => setCreating(false) : void 0
        },
        creating ? "__new__" : selectedRole.id
      ),
      !creating ? selectedIsRealRole ? /* @__PURE__ */ jsx(
        RoleMembers,
        {
          roleId: selectedRole.id,
          roleSystem: selectedRole.system,
          members: members.members,
          membersError: members.error,
          membersLoading: members.isLoading,
          onRetryMembers: members.refetch,
          onUpdateRoles: members.updateRoles
        }
      ) : /* @__PURE__ */ jsx("p", { className: "rounded-md border border-dashed p-3 text-xs text-muted-foreground", children: customRolesIncluded ? "This is an inherited platform default. Save it as a team role first to assign members to it." : (
        // Saving it as a team role IS creating a custom role, so the
        // advice above is the one thing this reader cannot do.
        `${CUSTOM_ROLES_NOT_INCLUDED} This inherited platform default can\u2019t be saved as a team role, so no members can be assigned to it.`
      ) }) : null
    ] }) : /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center rounded-md border border-dashed p-6 text-sm text-muted-foreground", children: customRolesIncluded ? "Select a role to edit, or create a new one." : "Select a role to view it." })
  ] });
}
var ACTION_LABEL2 = {
  "team.created": "Team created",
  "team.updated": "Team updated",
  "team.archived": "Team archived",
  "member.invited": "Member invited",
  "member.joined": "Member joined",
  "member.roles_updated": "Member roles changed",
  "member.status_updated": "Member status changed",
  "member.removed": "Member removed",
  "role.created": "Role created",
  "role.updated": "Role updated",
  "role.deleted": "Role deleted",
  "invite.created": "Invitation sent",
  "invite.resent": "Invitation resent",
  "invite.revoked": "Invitation revoked",
  "invite.accepted": "Invitation accepted"
};
function actionLabel(action) {
  const known = ACTION_LABEL2[action];
  if (known) return known;
  const words = action.replace(/[._]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Activity";
}
function TeamAuditLog({
  entries,
  teamId,
  actorName,
  isLoading,
  error,
  onRetry,
  hasMore,
  isLoadingMore,
  onLoadMore,
  loadMoreError,
  className
}) {
  if (error) return /* @__PURE__ */ jsx(ErrorState, { error, onRetry });
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", "aria-busy": true, children: Array.from({ length: 5 }).map((_, index) => /* @__PURE__ */ jsx(Skeleton, { className: "h-14 w-full" }, index)) });
  }
  if (entries.length === 0 && !(hasMore && onLoadMore)) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No activity yet",
        description: "Member, role, and invite changes will appear here with attribution."
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
    entries.length > 0 ? /* @__PURE__ */ jsx(
      AuditTrail,
      {
        events: toActivityLogEvents(entries).map((event, index) => {
          const entry = entries[index];
          return {
            ...event,
            function_name: actionLabel(entry?.action ?? ""),
            payload_summary: entry?.target && entry.target === teamId ? void 0 : event.payload_summary,
            created_by: entry?.actor ? actorName?.(entry.actor) ?? entry.actor : event.created_by
          };
        }),
        className
      }
    ) : /* @__PURE__ */ jsx("p", { className: "self-center text-sm text-muted-foreground", children: "No activity in the most recent window \u2014 older activity may exist." }),
    loadMoreError ? (
      // Stated where the button is, not as a toast: the reader is looking at
      // a truncated timeline and the only honest thing to say is that there
      // IS more and this attempt failed.
      /* @__PURE__ */ jsx("p", { role: "alert", className: "self-center text-xs text-destructive", children: "Couldn't load older activity \u2014 this isn't the end of the log. Try again." })
    ) : null,
    hasMore && onLoadMore ? /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "sm",
        className: "self-center",
        disabled: isLoadingMore,
        onClick: () => void onLoadMore(),
        children: isLoadingMore ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2 text-current" }),
          "Loading\u2026"
        ] }) : loadMoreError ? "Try again" : "Load older activity"
      }
    ) : null
  ] });
}
function TeamSwitcher({
  memberships,
  current,
  onSwitch,
  className
}) {
  const byKey = React.useMemo(
    () => new Map(memberships.map((membership) => [teamKey(membership.team), membership.team])),
    [memberships]
  );
  const handleChange = (key) => {
    const team = byKey.get(key);
    if (team && !sameTeam(team, current)) onSwitch(team);
  };
  return /* @__PURE__ */ jsxs(Select, { value: teamKey(current), onValueChange: handleChange, children: [
    /* @__PURE__ */ jsx(SelectTrigger, { className, "aria-label": "Switch team", children: /* @__PURE__ */ jsx(SelectValue, { placeholder: "Select team" }) }),
    /* @__PURE__ */ jsx(SelectContent, { children: memberships.map((membership) => {
      const { team } = membership;
      return /* @__PURE__ */ jsx(SelectItem, { value: teamKey(team), children: /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(
          Avatar,
          {
            src: team.avatarUrl,
            alt: team.name,
            size: "sm",
            fallback: memberInitials({ name: team.name })
          }
        ),
        /* @__PURE__ */ jsx("span", { className: team.archived ? "text-muted-foreground" : void 0, children: team.name }),
        team.archived ? (
          // A word, not just the dimming: colour alone is not a label,
          // and this one changes what the row means.
          /* @__PURE__ */ jsx("span", { className: "rounded-sm border px-1 text-[10px] uppercase tracking-wide text-muted-foreground", children: "Archived" })
        ) : null,
        typeof team.memberCount === "number" ? /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
          "\xB7 ",
          team.memberCount
        ] }) : null
      ] }) }, teamKey(team));
    }) })
  ] });
}
function PanelInner({
  resourceGroups,
  actions,
  scopes,
  defaultRoles,
  inviteSuggestions,
  memberships,
  onSwitchTeam,
  title,
  description,
  defaultTab = "members",
  className
}) {
  const { team, currentUserId } = useTeams();
  const members = useMembers();
  const roles = useRoles();
  const invites = useInvites();
  const audit = useTeamAudit();
  const [tab, setTab] = React.useState(defaultTab);
  const assignableRoles = roles.roles;
  const rolesError = roles.error;
  const retryRoles = roles.refetch;
  const suggestions = React.useMemo(() => {
    if (!inviteSuggestions) return void 0;
    const taken = /* @__PURE__ */ new Set();
    for (const member of members.members) if (member.email) taken.add(member.email.toLowerCase());
    for (const invite of invites.invites) {
      if (invite.status === InviteStatus.PENDING && invite.email) {
        taken.add(invite.email.toLowerCase());
      }
    }
    return inviteSuggestions.filter((person) => !taken.has(person.email.toLowerCase()));
  }, [inviteSuggestions, members.members, invites.invites]);
  const actorName = React.useCallback(
    (userId) => {
      if (currentUserId && userId === currentUserId) return "you";
      const member = members.members.find((candidate) => candidate.id === userId);
      return member ? member.name || member.email : void 0;
    },
    [currentUserId, members.members]
  );
  const inviteDialog = invites.canInvite ? /* @__PURE__ */ jsx(InviteMemberDialog, { roles: assignableRoles, suggestions, onInvite: invites.invite }) : null;
  const heading = title === void 0 ? team.name : title;
  const hasHeading = heading != null && heading !== "";
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4", className), children: [
    /* @__PURE__ */ jsxs("header", { className: "flex flex-wrap items-center gap-3", children: [
      hasHeading || description ? /* @__PURE__ */ jsxs("div", { className: "mr-auto min-w-0", children: [
        hasHeading ? /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold", children: heading }) : null,
        description ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: description }) : null
      ] }) : null,
      memberships && memberships.length > 1 && onSwitchTeam ? /* @__PURE__ */ jsx(
        TeamSwitcher,
        {
          memberships,
          current: team,
          onSwitch: onSwitchTeam,
          className: "w-56"
        }
      ) : null,
      inviteDialog ? /* @__PURE__ */ jsx("div", { className: "ml-auto", children: inviteDialog }) : null
    ] }),
    /* @__PURE__ */ jsxs(Tabs, { value: tab, onValueChange: (value) => setTab(value), children: [
      /* @__PURE__ */ jsxs(TabsList, { children: [
        /* @__PURE__ */ jsx(TabsTrigger, { value: "members", children: "Members" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "roles", children: "Roles" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "invites", children: "Invites" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "audit", children: "Audit log" })
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { value: "members", className: "pt-4", children: /* @__PURE__ */ jsx(
        MembersTable,
        {
          members: members.members,
          roles: assignableRoles,
          rolesError,
          onRetryRoles: retryRoles,
          currentUserId,
          isLoading: members.isLoading,
          error: members.error,
          onRetry: members.refetch,
          onUpdateRoles: members.updateRoles,
          onSetStatus: members.setStatus,
          onRemove: members.remove,
          emptyAction: inviteDialog
        }
      ) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "roles", className: "pt-4", children: /* @__PURE__ */ jsx(
        RolesPanel,
        {
          resourceGroups,
          actions,
          scopes,
          defaultRoles
        }
      ) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "invites", className: "pt-4", children: /* @__PURE__ */ jsx(
        InvitesTable,
        {
          invites: invites.invites,
          roles: assignableRoles,
          rolesError,
          onRetryRoles: retryRoles,
          isLoading: invites.isLoading,
          error: invites.error,
          onRetry: invites.refetch,
          onRevoke: invites.revoke,
          ...invites.canResend ? { onResend: invites.resend } : {},
          emptyAction: inviteDialog
        }
      ) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "audit", className: "pt-4", children: /* @__PURE__ */ jsx(
        TeamAuditLog,
        {
          entries: audit.entries,
          teamId: team.id,
          actorName,
          isLoading: audit.isLoading,
          error: audit.error,
          onRetry: audit.refetch,
          hasMore: audit.hasMore,
          isLoadingMore: audit.isLoadingMore,
          onLoadMore: audit.loadMore,
          loadMoreError: audit.loadMoreError
        }
      ) })
    ] })
  ] });
}
function TeamManagementPanel(props) {
  const {
    api,
    team,
    currentUserId,
    can,
    permissions,
    rolePermissions,
    ungated,
    auditQuery,
    refreshToken,
    resourceGroups,
    actions,
    scopes,
    defaultRoles,
    inviteSuggestions,
    memberships,
    onSwitchTeam,
    title,
    description,
    defaultTab,
    className
  } = props;
  return /* @__PURE__ */ jsx(ErrorBoundary, { boundary: "teams:management-panel", children: /* @__PURE__ */ jsx(
    TeamsProvider,
    {
      api,
      team,
      currentUserId,
      can,
      permissions,
      rolePermissions,
      ungated,
      auditQuery,
      refreshToken,
      children: /* @__PURE__ */ jsx(
        PanelInner,
        {
          resourceGroups,
          actions,
          scopes,
          defaultRoles,
          inviteSuggestions,
          memberships,
          onSwitchTeam,
          title,
          description,
          defaultTab,
          className
        }
      )
    }
  ) });
}
function rows(data) {
  if (Array.isArray(data)) return data;
  const items = data?.items;
  return Array.isArray(items) ? items : [];
}
function nextCursor(data) {
  if (Array.isArray(data)) return void 0;
  const cursor = data?.next_cursor;
  return typeof cursor === "string" && cursor !== "" ? cursor : void 0;
}
var PAGE_LIMIT = 200;
var MAX_LIST_PAGES = 20;
var FRESH = { cacheTtlMs: 0 };
async function listAll(client, path, extra) {
  const out = [];
  let cursor;
  for (let page = 0; page < MAX_LIST_PAGES; page += 1) {
    const data = unwrapData(
      await client.get(path, { query: { ...extra, limit: PAGE_LIMIT, cursor }, ...FRESH })
    );
    out.push(...rows(data));
    cursor = nextCursor(data);
    if (!cursor) return out;
  }
  throw new Error(
    `This list is larger than ${MAX_LIST_PAGES * PAGE_LIMIT} rows (${path}), so it cannot be shown in full. Showing part of it would hide members, roles or invitations without saying so \u2014 narrow the team or raise the page bound.`
  );
}
var strs = (v) => Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
function toIso(v) {
  if (v == null) return void 0;
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  if (Number.isFinite(n)) return new Date(n < 1e12 ? n * 1e3 : n).toISOString();
  return typeof v === "string" ? v : void 0;
}
function memberFromWire(w) {
  return {
    id: asString(w.user_id) ?? asString(w.id) ?? "",
    name: asString(w.display_name) ?? asString(w.email) ?? asString(w.user_id) ?? asString(w.id) ?? "Unknown member",
    email: asString(w.email),
    avatarUrl: asString(w.avatar_url),
    roleIds: strs(w.role_ids ?? w.roleIds),
    status: asString(w.status) ?? "active",
    joinedAt: toIso(w.joined_at ?? w.created_at),
    lastActiveAt: toIso(w.last_active_at)
  };
}
function roleFromWire(w) {
  return {
    id: asString(w.id) ?? asString(w.role_id) ?? "",
    name: asString(w.name) ?? asString(w.id) ?? "Role",
    description: asString(w.description),
    permissions: w.permissions ?? w.role_permissions ?? {},
    system: Boolean(w.system ?? w.is_system),
    memberCount: typeof w.member_count === "number" ? w.member_count : typeof w.memberCount === "number" ? w.memberCount : void 0
  };
}
function inviteFromWire(w) {
  return {
    id: asString(w.id) ?? asString(w.invite_id) ?? "",
    email: asString(w.email) ?? "",
    roleIds: strs(w.role_ids ?? w.roleIds),
    status: asString(w.status) ?? "pending",
    invitedBy: asString(w.invited_by),
    invitedAt: toIso(w.invited_at ?? w.created_at),
    expiresAt: toIso(w.expires_at),
    emailSent: typeof w.email_sent === "boolean" ? w.email_sent : void 0,
    acceptPath: asString(w.accept_path)
  };
}
function auditFromWire(w) {
  return {
    id: asString(w.id) ?? "",
    action: asString(w.action) ?? asString(w.function_name) ?? "",
    summary: asString(w.summary) ?? asString(w.message_summary) ?? "",
    at: toIso(w.at ?? w.created_at) ?? "",
    actor: asString(w.actor) ?? asString(w.created_by),
    target: asString(w.target) ?? asString(w.payload_summary),
    severity: asString(w.severity),
    metadata: w.metadata ?? void 0
  };
}
function tenantFromWire(value) {
  const w = value ?? {};
  return {
    customerId: asString(w.customerId) ?? asString(w.customer_id) ?? null,
    orgId: asString(w.orgId) ?? asString(w.org_id) ?? null,
    tenantId: asString(w.tenantId) ?? asString(w.tenant_id) ?? null
  };
}
function teamRefFromWire(w) {
  return {
    id: asString(w.id) ?? asString(w.team_id) ?? "",
    name: asString(w.name) ?? asString(w.id) ?? "Team",
    tenant: {
      customerId: asString(w.customer_id) ?? null,
      orgId: asString(w.org_id) ?? null,
      tenantId: asString(w.tenant_id) ?? null
    },
    avatarUrl: asString(w.avatar_url),
    description: asString(w.description),
    isDefault: typeof w.is_default === "boolean" ? w.is_default : void 0,
    // `status` is the soft-delete flag. Only the admin list ever carries an
    // archived row, and it has to reach the UI or the operator cannot tell why
    // a team's members and roles are frozen.
    archived: asString(w.status) === "archived" ? true : void 0,
    // On `TeamRef` and on the wire, and mapped by nobody — so the switcher's
    // "· N" was dead markup on every mount.
    memberCount: typeof w.member_count === "number" ? w.member_count : void 0
  };
}
function membershipFromWire(w) {
  const team = w.team ?? void 0;
  const id = asString(team?.id) ?? asString(w.team_id) ?? "";
  const name = asString(team?.name) ?? asString(w.team_name) ?? id;
  const tenant = tenantFromWire(team?.tenant ?? w.tenant ?? w);
  return {
    team: { id, name, tenant, avatarUrl: asString(team?.avatar_url ?? w.avatar_url) },
    roleIds: strs(w.role_ids ?? w.roleIds)
  };
}
function createApiTeamsApi(client, options = {}) {
  const readBase = options.readBasePath ?? options.basePath ?? "/teams";
  const writeBase = options.writeBasePath ?? options.basePath ?? "/teams";
  const readPath = (team) => `${readBase}/${encodeURIComponent(team.id)}`;
  const writePath = (team) => `${writeBase}/${encodeURIComponent(team.id)}`;
  const canIssueInvites = options.canIssueInvites ?? true;
  const tenancyQuery = options.tenancyQuery;
  const tenancyBody = options.tenancyBody;
  const invites = {
    async inviteMember(team, input) {
      return inviteFromWire(
        unwrapData(
          await client.post(`${writePath(team)}/invites`, {
            email: input.email,
            role_ids: input.roleIds,
            message: input.message
          })
        )
      );
    },
    async resendInvite(team, inviteId) {
      return inviteFromWire(
        unwrapData(
          await client.post(`${writePath(team)}/invites/${encodeURIComponent(inviteId)}/resend`)
        )
      );
    }
  };
  return {
    ...canIssueInvites ? invites : {},
    // ── Teams ──────────────────────────────────────────────────────────────────
    async listTeams() {
      return (await listAll(client, readBase, tenancyQuery)).map(teamRefFromWire);
    },
    async createTeam(input) {
      return teamRefFromWire(
        unwrapData(
          await client.post(writeBase, {
            ...tenancyBody ?? {},
            name: input.name,
            description: input.description,
            // Only when the caller names one: the customer-facing API derives
            // the owner from the token and rejects unknown fields loudly.
            ...input.ownerUserId ? { owner_user_id: input.ownerUserId } : {}
          })
        )
      );
    },
    async updateTeam(team, input) {
      const body = {};
      if (input.name !== void 0) body.name = input.name;
      if (input.description !== void 0) body.description = input.description;
      return teamRefFromWire(unwrapData(await client.patch(writePath(team), body)));
    },
    async archiveTeam(team) {
      unwrapData(await client.delete(writePath(team)));
    },
    // ── Members ──────────────────────────────────────────────────────────────
    async listMembers(team) {
      return (await listAll(client, `${readPath(team)}/members`)).map(memberFromWire);
    },
    async updateMemberRoles(team, memberId, roleIds) {
      return memberFromWire(
        unwrapData(
          await client.patch(`${writePath(team)}/members/${encodeURIComponent(memberId)}`, {
            role_ids: roleIds
          })
        )
      );
    },
    async setMemberStatus(team, memberId, status) {
      return memberFromWire(
        unwrapData(
          await client.patch(`${writePath(team)}/members/${encodeURIComponent(memberId)}`, {
            status
          })
        )
      );
    },
    async removeMember(team, memberId) {
      unwrapData(await client.delete(`${writePath(team)}/members/${encodeURIComponent(memberId)}`));
    },
    // ── Roles ────────────────────────────────────────────────────────────────
    async listRoles(team) {
      return (await listAll(client, `${readPath(team)}/roles`)).map(roleFromWire);
    },
    async createRole(team, input) {
      return roleFromWire(unwrapData(await client.post(`${writePath(team)}/roles`, input)));
    },
    async updateRole(team, roleId, patch) {
      return roleFromWire(
        unwrapData(await client.patch(`${writePath(team)}/roles/${encodeURIComponent(roleId)}`, patch))
      );
    },
    async deleteRole(team, roleId) {
      unwrapData(await client.delete(`${writePath(team)}/roles/${encodeURIComponent(roleId)}`));
    },
    // ── Invites ──────────────────────────────────────────────────────────────
    async listInvites(team) {
      return (await listAll(client, `${readPath(team)}/invites`)).map(inviteFromWire);
    },
    // `inviteMember` / `resendInvite` are spread in above, and only when this
    // mount can issue them — a literal key here would win over the spread and
    // put them back on a backend that has no such route.
    async revokeInvite(team, inviteId) {
      unwrapData(await client.delete(`${writePath(team)}/invites/${encodeURIComponent(inviteId)}`));
    },
    // ── Audit ────────────────────────────────────────────────────────────────
    // The one list that is NOT drained to exhaustion: an audit trail is
    // unbounded by nature, so the page + its cursor are handed up and the log
    // asks for more only when the reader does.
    async listAuditEvents(team, query = {}) {
      const res = unwrapResponse(
        await client.get(`${readPath(team)}/audit`, {
          // The audit feed is the other route the admin mount declares the
          // tenancy REQUIRED on (the gateway answers 400 without it).
          query: { ...tenancyQuery, limit: query.limit, cursor: query.cursor },
          ...FRESH
        })
      );
      return {
        entries: rows(res.data).map(auditFromWire),
        nextCursor: nextCursor(res.data)
      };
    },
    // ── Memberships (team switcher) ──────────────────────────────────────────
    // `GET /teams/memberships` returns the CALLER's membership rows — it reads
    // the subject off the token and has no `userId` parameter, so sending one
    // did nothing and implied a lookup the route does not offer. The `userId`
    // argument stays on the port (it is part of `TeamsApi`) and is asserted
    // against the caller instead, so a host asking for someone else's
    // memberships gets an empty list rather than silently getting its own.
    async listMemberships(userId) {
      const wire = await listAll(client, `${readBase}/memberships`);
      return wire.filter((row) => !userId || asString(row.user_id) === void 0 || asString(row.user_id) === userId).map(membershipFromWire);
    }
  };
}

// src/data/memberNames.ts
function isUnnamed(member) {
  const name = (member.name ?? "").trim();
  if (!name) return true;
  if (name === member.id) return true;
  return name === (member.email ?? "").trim();
}
function indexPeople(people) {
  const known = /* @__PURE__ */ new Map();
  for (const person of people) {
    const rawName = (person.name ?? "").trim();
    const name = rawName && rawName !== person.email ? rawName : void 0;
    const email = (person.email ?? "").trim() || void 0;
    if (!name && !email) continue;
    const entry = { ...name ? { name } : {}, ...email ? { email } : {} };
    const id = person.user_id || person.id;
    if (id) known.set(id, entry);
    if (email) known.set(email.toLowerCase(), entry);
  }
  return known;
}
function applyName(member, names, self) {
  if (self?.id && member.id === self.id) {
    return {
      ...member,
      name: self.name ?? member.name,
      email: member.email ?? self.email ?? void 0
    };
  }
  const known = names.get(member.id) ?? (member.email ? names.get(member.email.toLowerCase()) : void 0);
  if (known && (isUnnamed(member) || !member.email)) {
    return {
      ...member,
      // Each half only fills a gap: a name the row already has is never
      // replaced, and neither is an email.
      ...known.name && isUnnamed(member) ? { name: known.name } : {},
      ...member.email ? {} : known.email ? { email: known.email } : {}
    };
  }
  if ((member.name ?? "").trim().toLowerCase() === "seed owner") {
    const local = member.email ? member.email.split("@")[0] : "";
    return { ...member, name: local || member.email || member.name };
  }
  return member;
}
function withMemberNames(api, options) {
  const { listPeople, self = null } = options;
  const directory = async () => {
    try {
      return indexPeople(await listPeople());
    } catch {
      return /* @__PURE__ */ new Map();
    }
  };
  const needsDirectory = (member) => member.id !== self?.id && (isUnnamed(member) || !member.email);
  const namedOne = async (member) => applyName(member, needsDirectory(member) ? await directory() : /* @__PURE__ */ new Map(), self);
  return {
    ...api,
    async listMembers(team) {
      const members = await api.listMembers(team);
      const names = members.some(needsDirectory) ? await directory() : /* @__PURE__ */ new Map();
      return members.map((member) => applyName(member, names, self));
    },
    async updateMemberRoles(...args) {
      return namedOne(await api.updateMemberRoles(...args));
    },
    async setMemberStatus(...args) {
      return namedOne(await api.setMemberStatus(...args));
    }
  };
}

export { CUSTOM_ROLES_NOT_INCLUDED, InviteAcceptanceLink, InviteMemberDialog, InvitesTable, MembersTable, RoleChips, RoleEditor, RoleMembers, RolesList, RolesPanel, RolesUnavailableNotice, TEAMS_PACKAGE, TeamAuditLog, TeamDataProvider, TeamManagementPanel, TeamSwitcher, TeamsProvider, canGrantRole, createApiTeamsApi, customRolesRefusalMessage, groupRolesBySource, isCustomRolesRefusal, isPlatformRole, isUnnamed, mergeDefaultRoles, teamRefusalMessage, useCustomRolesIncluded, useInvites, useMembers, useOptionalTeams, useRoles, useTeamAudit, useTeamCan, useTeamData, useTeams, withMemberNames };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map